import { Link } from 'react-router-dom';
import {
  telHref, formatPhone, directionsUrl, buildWhatsAppUrl, whatsappMessage,
  type Branch, type Menu, type SiteSettings,
} from '@cng/shared';
import { InstagramIcon, WhatsAppIcon, FacebookIcon, YouTubeIcon, PinIcon, PhoneIcon } from '@/components/common/Icons';

interface FooterProps {
  settings: SiteSettings;
  menu: Menu | null;
  branches: Branch[];
}

/**
 * Reproduces the original `.theme-footer` markup. Branch columns are generated
 * from the Branch collection, so adding a third salon needs no code change.
 */
export function Footer({ settings, menu, branches }: FooterProps) {
  const links = (menu?.items ?? []).filter((i) => i.enabled).sort((a, b) => a.order - b.order);

  const socials = [
    { url: settings.instagramUrl, label: 'Instagram', Icon: InstagramIcon },
    { url: settings.facebookUrl, label: 'Facebook', Icon: FacebookIcon },
    { url: settings.youtubeUrl, label: 'YouTube', Icon: YouTubeIcon },
  ].filter((s) => !!s.url);

  // Built through the shared helper like every other WhatsApp link, so the
  // number and the wording both come from settings rather than this component.
  const whatsapp = settings.whatsappNumber
    ? buildWhatsAppUrl({
      phone: settings.whatsappNumber,
      message: whatsappMessage('generalEnquiry', {}, settings.whatsappTemplates),
    })
    : '';

  return (
    <footer className="theme-footer" data-branch-count={branches.length}>
      <div className="footer-inner">
        <div className="foot-brand">
          <h2>{settings.businessName}</h2>
          {settings.footerDescription ? (
            <p
              className="foot-desc"
              // Stored copy contains <span class="highlight-loc"> emphasis, matching the original.
              dangerouslySetInnerHTML={{ __html: settings.footerDescription }}
            />
          ) : null}
          {(socials.length > 0 || whatsapp) && (
            <div className="social-links">
              {socials.map(({ url, label, Icon }) => (
                <a
                  key={label}
                  className="social-icon"
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${settings.businessName} on ${label}`}
                >
                  <Icon />
                </a>
              ))}
              {whatsapp ? (
                <a
                  className="social-icon"
                  href={whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Message us on WhatsApp"
                >
                  <WhatsAppIcon />
                </a>
              ) : null}
            </div>
          )}
        </div>

        {links.length > 0 && (
          <div className="foot-links">
            <h3 className="foot-head">{menu?.heading || 'Quick Links'}</h3>
            <ul>
              {links.map((item) => {
                const isExternal = item.type === 'external' || /^https?:\/\//i.test(item.value);
                return (
                  <li key={`${item.label}-${item.value}`}>
                    {isExternal ? (
                      <a
                        href={item.value}
                        target={item.newTab ? '_blank' : undefined}
                        rel={item.newTab ? 'noopener noreferrer' : undefined}
                      >
                        {item.label} <span aria-hidden="true">&rarr;</span>
                      </a>
                    ) : (
                      <Link to={item.value}>
                        {item.label} <span aria-hidden="true">&rarr;</span>
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {branches.map((branch) => (
          <div className="foot-contact" key={branch._id}>
            <h3 className="foot-head">{branch.shortName || branch.name}</h3>
            <div className="contact-item">
              <div className="icon-box" aria-hidden="true"><PinIcon /></div>
              <span>
                <strong>{settings.businessName} Family Salon</strong>
                <br />
                {branch.addressLines.map((line, i) => (
                  <span key={line}>
                    {line}
                    {i < branch.addressLines.length - 1 ? <br /> : null}
                  </span>
                ))}
              </span>
            </div>
            {branch.phone ? (
              <div className="contact-item">
                <div className="icon-box" aria-hidden="true"><PhoneIcon /></div>
                <span>
                  <a href={telHref(branch.phone)}>
                    {branch.phoneDisplay || formatPhone(branch.phone)}
                  </a>
                </span>
              </div>
            ) : null}
            <a
              className="dir-link"
              href={directionsUrl(branch)}
              target="_blank"
              rel="noopener noreferrer"
            >
              Get Directions
            </a>
          </div>
        ))}
      </div>

      <div className="footer-bottom">
        <p>{settings.copyrightText || `© ${new Date().getFullYear()} ${settings.businessName}. All Rights Reserved.`}</p>
        {settings.seoFooterText ? <p className="seo-text">{settings.seoFooterText}</p> : null}
      </div>
    </footer>
  );
}
